import { Component, inject, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { ErrorDialogComponent } from './shared/error-dialog/error-dialog';
import { CreditosInsuficientesDialogComponent } from './shared/creditos-insuficientes-dialog/creditos-insuficientes-dialog';
import { InteresseDialogComponent } from './shared/interesse-dialog/interesse-dialog';
import { CookieConsentComponent } from './shared/cookie-consent/cookie-consent';
import { WyLoaderComponent } from './shared/loader/loader';
import { I18nService } from './core/services/i18n.service';
import { MetaPixelService } from './core/services/meta-pixel.service';
import { AtribuicaoService } from './core/services/atribuicao.service';
import { CookieConsentService } from './core/services/cookie-consent.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ErrorDialogComponent,
    CreditosInsuficientesDialogComponent,
    InteresseDialogComponent,
    CookieConsentComponent,
    WyLoaderComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected readonly title = signal('Wydo');
  protected readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly metaPixel = inject(MetaPixelService);
  private readonly atribuicao = inject(AtribuicaoService);
  private readonly consent = inject(CookieConsentService);

  // Loader de navegação: cobre o intervalo entre clicar e o chunk lazy da rota montar,
  // para nunca deixar a tela em branco na transição. Só aparece se a navegação passar de
  // ~120ms (evita flash quando o chunk já está em cache e a troca é instantânea).
  protected readonly navigating = signal(false);
  private navTimer: ReturnType<typeof setTimeout> | null = null;
  // A primeira navegação (boot inicial) já tem o spinner estático do index.html cobrindo
  // a espera — mostrar este loader também dava a impressão de "2 spinners" em sequência.
  private isFirstNavigation = true;

  constructor() {
    this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        if (this.isFirstNavigation) return;
        this.navTimer = setTimeout(() => this.navigating.set(true), 120);
      } else if (e instanceof NavigationEnd) {
        this.isFirstNavigation = false;
        if (this.navTimer) {
          clearTimeout(this.navTimer);
          this.navTimer = null;
        }
        this.navigating.set(false);
        // Guarda de onde a pessoa veio antes de qualquer outra coisa: se ela entrou pelo anúncio,
        // é aqui que os parâmetros da URL ainda estão disponíveis.
        this.atribuicao.capturar(e.urlAfterRedirects);
        // Rotas de campanha sobem o rastreamento sem banner. Precisa vir antes do PageView,
        // senão o fbq ainda não existe na primeira rota e o evento se perde.
        this.consent.aplicarRota(e.urlAfterRedirects);
        // SPA: só o load inicial do documento gera pageview de graça. Cada rota resolvida
        // (inclusive a de destino de um redirect de guard) precisa do próprio PageView —
        // vira no-op sem consentimento de publicidade (ver MetaPixelService).
        this.metaPixel.trackPageView();
      } else if (e instanceof NavigationCancel || e instanceof NavigationError) {
        this.isFirstNavigation = false;
        if (this.navTimer) {
          clearTimeout(this.navTimer);
          this.navTimer = null;
        }
        this.navigating.set(false);
      }
    });
  }
}
